import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download } from "lucide-react";

const CATS = [
  { key: "visa", label: "Visas" },
  { key: "insurance", label: "Insurance" },
  { key: "voucher", label: "Vouchers" },
  { key: "other", label: "Other" },
];

export default function DocumentsPage() {
  const { tripId } = useParams();
  const { API, authHeaders, token, user } = useAuth();
  const { trip } = useContext(TripCtx);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", category: "voucher" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/trips/${tripId}/documents`, { headers: authHeaders }).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [tripId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    if (!form.title.trim()) return toast.error("Add a title");
    setBusy(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("file", file);
    try {
      await axios.post(`${API}/trips/${tripId}/documents`, fd,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      setForm({ title: "", category: "voucher" }); setFile(null);
      load();
      toast.success("Uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this document?")) return;
    await axios.delete(`${API}/documents/${id}`, { headers: authHeaders });
    load();
  };

  const grouped = CATS.map((c) => ({ ...c, items: docs.filter((d) => (d.category || "other") === c.key) }));

  return (
    <div data-testid="documents-page" className="py-4">
      <p className="tcm-eyebrow">{trip.destination}</p>
      <h1 className="font-display text-4xl mt-2">Documents</h1>
      <hr className="tcm-gold-rule w-16 my-6" />

      {!trip.archived && (
        <form onSubmit={submit} className="tcm-double-frame mb-8">
          <div className="tcm-inner p-5 space-y-3">
            <p className="tcm-eyebrow">Upload a document</p>
            <div><label className="tcm-eyebrow block mb-1">Title</label>
              <input data-testid="doc-title" className="tcm-input" placeholder="e.g. Italy travel insurance"
                     required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="tcm-eyebrow block mb-1">Category</label>
                <select data-testid="doc-category" className="tcm-input" value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATS.map((c) => <option key={c.key} value={c.key}>{c.label.replace(/s$/, "")}</option>)}
                </select></div>
              <div><label className="tcm-eyebrow block mb-1">File</label>
                <input data-testid="doc-file" type="file" className="tcm-input"
                       accept="application/pdf,image/*"
                       onChange={(e) => setFile(e.target.files[0])} /></div>
            </div>
            <button data-testid="doc-submit" disabled={busy} className="tcm-btn-primary w-full inline-flex items-center justify-center gap-2">
              <Upload size={13} /> {busy ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-8">
        {grouped.map((g) => (
          <section key={g.key} data-testid={`docs-${g.key}`}>
            <p className="tcm-eyebrow mb-3" style={{ color: "var(--tcm-gold-700)" }}>{g.label}</p>
            {g.items.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)]">None yet</p>}
            <div className="space-y-2">
              {g.items.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`}
                     className="flex items-center gap-3 border-t border-[color:var(--tcm-navy-15)] pt-3">
                  <FileText size={16} strokeWidth={1.5} style={{ color: "var(--tcm-gold-700)" }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight truncate">{d.title}</p>
                    <p className="tcm-eyebrow mt-0.5">
                      {d.uploaded_by_name} · {new Date(d.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {(d.size/1024).toFixed(0)} KB
                    </p>
                  </div>
                  <a href={`${API}/documents/${d.id}/download?auth=${token}`} target="_blank" rel="noreferrer"
                     data-testid={`dl-${d.id}`} className="tcm-btn-outline inline-flex items-center gap-1.5 text-[11px] py-1.5 px-2.5">
                    <Download size={12} />
                  </a>
                  {(user?.role !== "lead_client" || d.uploaded_by === user.id) && (
                    <button onClick={() => remove(d.id)} data-testid={`del-${d.id}`}
                            className="tcm-btn-outline inline-flex items-center gap-1.5 text-[11px] py-1.5 px-2.5">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
